import React from 'react';
import {Box, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import SchoolDashboardShell from '../components/SchoolDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {NAVY, PageHeader, StatCard, ScoreBar, BandChip, GuidanceBanner, headCell, bodyCell, MARKER_SECONDARY, MARKER_PRIMARY, readinessBandColor} from '../components/widgets.comp';
import { useDashboard } from '../../../context/DashboardContext';
import {isSuppressedItem, isSuppressedKpi, SUPPRESSED_COPY, whole} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';
import {READINESS_BAND_COLORS} from '../../../utils/constants';

import {UNIT, fmtValue} from '../../DashboardCommon/units';
const Panel = ({title, children}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
    }}>
        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 2.5}}>{title}</Typography>
        {children}
    </Box>
);

// The grades table (SD-13-08) — every column sorts from its header, and filters once the
// school has enough grades (QA 9 Oct 2026). Its own component so the hook runs below the
// page's loading branches. A suppressed grade has no score or flag to sort by.
const GRADE_COLUMNS = [
    {key: 'grade', label: 'Grade', type: 'text'},
    {key: 'score', label: 'Score', type: 'number', accessor: (g) => (g.suppressed ? null : g.score)},
    {key: 'flag', label: 'Flag', type: 'band', order: ['Lowest', ...Object.keys(READINESS_BAND_COLORS)], accessor: (g) => (g.suppressed ? null : g.flag)},
];
const GradesTable = ({rows}) => {
    const t = useTableControls(rows, GRADE_COLUMNS);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <SortFilterHeaderCell {...t.headerProps('grade')} sx={{...headCell, py: 1.25}}/>
                            <SortFilterHeaderCell {...t.headerProps('score')} sx={{...headCell, py: 1.25, textAlign: 'center'}}/>
                            <SortFilterHeaderCell {...t.headerProps('flag')} sx={{...headCell, py: 1.25}}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} sx={{...bodyCell, textAlign: 'center', color: '#6A7282', py: 3}}>No grade matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((g) => (
                            <TableRow key={g.grade}>
                                <TableCell sx={{...bodyCell, fontWeight: 700, color: NAVY}}>{g.grade}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center', fontWeight: 700}}>
                                    {g.suppressed ? <SuppressedValue variant="cell"/> : (g.score === null ? '—' : g.score)}
                                </TableCell>
                                <TableCell sx={bodyCell}>
                                    {g.flag === 'Lowest'
                                        ? <BandChip label="Lowest" color="#E04535"/>
                                        : (g.flag && <BandChip label={g.flag} color={readinessBandColor(g.flag)}/>)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const SCHOOL_OVERVIEW_SECTION_CODE = 'S_4';
const CARD_SECTION = SCHOOL_OVERVIEW_SECTION_CODE;

const SchoolSummaryPage = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTION });

    // Get the S_4 section
    const s4Section = dashboardData?.cards?.sections?.find(s => s.section_code === SCHOOL_OVERVIEW_SECTION_CODE);
    const s4Cards = s4Section?.cards || [];

    // Extract dynamic TitleCard if available
    const titleCard = s4Cards.find(c => c.card_type === 'TitleCard');
    const headerTitle = titleCard?.title || 'School Baseline Overview';
    const headerSubtitle = titleCard?.footer;

    // Extract dynamic ORGCard data if available
    const orgCard = s4Cards.find(c => c.card_type === 'ORGCard');
    const dynamicInfo = orgCard ? orgCard.table_objects.map(obj => ({
        label: obj.column_name,
        value: obj.values?.[0] || '—'
    })) : [];

    // Extract dynamic KPICards if available
    const kpiCards = s4Cards.filter(c => c.card_type === 'KPICard') || [];
    const displayKpiCards = [];
    if (kpiCards.length > 0) {
        kpiCards.forEach(c => {
            let subText = '';

            if (c.kpi_name?.toUpperCase()?.includes('PARTICIPATION')) {
                const participants = c.kpi_add_infos?.find(i => i.info_name === 'Participants')?.info_value;
                const total = c.kpi_add_infos?.find(i => i.info_name === 'Total')?.info_value;
                subText = (participants !== undefined && total !== undefined) ? `${participants} / ${total}` : '';
            } else {
                // Comparison w/ national and global avgs — the card's own
                // kpi_add_infos (Data Map SD-13-02 / SD-13-03). A missing one
                // is left out rather than shown as a placeholder.
                const avgOf = (name) => {
                    const v = c.kpi_add_infos?.find(i => String(i.info_name || '').replace(/\.$/, '').toLowerCase() === name)?.info_value;
                    return whole(v);
                };
                const national = avgOf('national avg');
                const global = avgOf('global avg');
                // "%" belongs to the risk and participation KPIs only; the composite is a score (QA 9 Oct).
                const kpiUnit = (c.card_code === 'C_4' || /risk|participation/i.test(c.kpi_name || '')) ? UNIT.PERCENT : UNIT.SCORE;
                subText = (national !== null || global !== null) ? (
                    <>
                        {national !== null && <>National avg <strong>{fmtValue(national, kpiUnit)}</strong></>}
                        {national !== null && global !== null && <>&nbsp;&nbsp;</>}
                        {global !== null && <>Global avg <strong>{fmtValue(global, kpiUnit)}</strong></>}
                    </>
                ) : '';
            }

            const isPercentKpi = c.kpi_name?.toUpperCase()?.includes('PARTICIPATION') || c.kpi_name?.toUpperCase()?.includes('RISK');
            // Under the n floor (suppressed): no value, no band — the copy says why.
            const suppressed = isSuppressedKpi(c);
            let displayVal;
            if (suppressed || c.kpi_value === null || c.kpi_value === undefined) {
                displayVal = '—';
            } else if (typeof c.kpi_value === 'number') {
                displayVal = isPercentKpi ? `${Math.round(c.kpi_value)}%` : Math.round(c.kpi_value);
            } else if (typeof c.kpi_value === 'string') {
                displayVal = isPercentKpi && !c.kpi_value.endsWith('%') ? `${c.kpi_value}%` : c.kpi_value;
            } else {
                displayVal = c.kpi_value;
            }

            const isRiskKpi = c.kpi_name?.toUpperCase()?.includes('RISK') || c.kpi_name?.toUpperCase()?.includes('EXPERIENCE');

            displayKpiCards.push({
                label: c.kpi_name,
                value: String(displayVal),
                band: suppressed ? null : c.kpi_band,
                bandColorOverride: isRiskKpi ? undefined : readinessBandColor(c.kpi_band),
                sub: suppressed ? SUPPRESSED_COPY : subText
            });
        });
    }

    // Extract dynamic BarChartBMCard (competencies) if available
    const barChartCard = s4Cards.find(c => c.card_type === 'BarChartBMCard' && (c.title?.toUpperCase()?.includes('AREA') || c.title?.toUpperCase()?.includes('COMPETENCY')));
    const areaScoresTitle = barChartCard?.title;
    const dynamicAreaScores = barChartCard?.items ? barChartCard.items.slice(0, 3).map(item => ({
        label: item.label,
        suppressed: isSuppressedItem(item),
        score: item.value,
        band: item.band,
        national: item.benchmark_values?.find(b => b.name?.includes('National'))?.value,
        global: item.benchmark_values?.find(b => b.name?.includes('Global'))?.value
    })) : [];

    // Extract dynamic BarChartBMCard (context & support) via card_code = C_6a
    const c6aCard = s4Cards.find(c => c.card_code === 'C_6a' || c.card_code === 'C_6A');
    const c6aTitle = c6aCard?.title;
    const dynamicContextScores = c6aCard?.items ? c6aCard.items.slice(0, 3).map(item => ({
        label: item.label,
        suppressed: isSuppressedItem(item),
        score: item.value,
        band: item.band,
        national: item.benchmark_values?.find(b => b.name?.includes('National'))?.value,
        global: item.benchmark_values?.find(b => b.name?.includes('Global'))?.value
    })) : [];

    // Extract dynamic BarChartBMCard (risks) if available (card_code C_7 or card_type BarChartBMCard/BarChartCard with RISK in title)
    const riskCard = s4Cards.find(c => c.card_code === 'C_7' || ((c.card_type === 'BarChartBMCard' || c.card_type === 'BarChartCard') && c.title?.toUpperCase()?.includes('RISK')));
    // Static copy is frontend-owned: "Cyber Risks" stands alone (no hyphen), whatever the card's title says.
    const topRisksTitle = 'Top Child Cyber Risks';
    const dynamicTopRisks = riskCard?.items ? riskCard.items.slice(0, 5).map(item => {
        return {
            label: item.label,
            suppressed: isSuppressedItem(item),
            pct: item.value,                                   // a prevalence — ScoreBar prints it with "%" (unit="percent")
            band: item.band,
            national: item.benchmark_values?.find(b => b.name?.toLowerCase().includes('national'))?.value,
            global: item.benchmark_values?.find(b => b.name?.toLowerCase().includes('global'))?.value
        };
    }) : [];

    // Extract dynamic BarChartBMCard (grades) if available. A grade under the n floor
    // (suppressed) keeps its row — its participation is real — but shows no score or band.
    const gradesCard = s4Cards.find(c => c.card_code === 'C_8' || c.title?.toUpperCase()?.includes('GRADE'));
    const gradesTitle = gradesCard?.title;
    const dynamicGrades = gradesCard?.items ? gradesCard.items.map(item => {
        const coverageVal = item.benchmark_values?.find(b => b.name === 'Coverage')?.value;
        const suppressed = isSuppressedItem(item);
        return {
            grade: item.label,
            suppressed,
            score: suppressed ? null : whole(item.value),
            flag: suppressed ? null : (item.band ? item.band : (coverageVal !== undefined && coverageVal !== null ? `${coverageVal}% part.` : '—'))
        };
    }) : [];

    // Guidance banner fields
    const compositeKpi = kpiCards.find(c => c.kpi_name?.toUpperCase()?.includes('COMPOSITE'));
    const compositeSuppressed = isSuppressedKpi(compositeKpi);
    const compositeVal = !compositeSuppressed && typeof compositeKpi?.kpi_value === 'number' ? Number(compositeKpi.kpi_value.toFixed(2)) : (compositeKpi?.kpi_value ?? '—');
    const compositeBand = (!compositeSuppressed && compositeKpi?.kpi_band) || "—";
    // The comparison uses the composite card's own benchmark (kpi_add_infos,
    // Data Map SD-13-02) — National if the datahouse sends one, else Global.
    // A fixed 60 stood here before and read as a real national benchmark on
    // every school, including schools whose card carries no national average.
    const compositeAvg = (name) => {
        const v = compositeKpi?.kpi_add_infos?.find(i => String(i.info_name || '').replace(/\.$/, '').toLowerCase() === name)?.info_value;
        return (v === undefined || v === null || v === '') ? null : Math.round(Number(v));
    };
    const nationalComposite = compositeAvg('national avg');
    const globalComposite = compositeAvg('global avg');
    const vsText = nationalComposite !== null
        ? ` vs national ${nationalComposite}`
        : (globalComposite !== null ? ` vs global ${globalComposite}` : '');
    const guidanceText = compositeSuppressed
        ? `School composite is not shown — ${SUPPRESSED_COPY}. Read competency and risk separately; check data confidence before comparing grades.`
        : `School composite is ${compositeVal} (${compositeBand})${vsText}. Read competency and risk separately; check data confidence before comparing grades.`;

    if (loading) {
        return (
            <SchoolDashboardShell needsAssessmentResults>
                <CircularLoader />
            </SchoolDashboardShell>
        );
    }

    if (!dashboardData || !s4Section) {
        return (
            <SchoolDashboardShell needsAssessmentResults>
                <NoDataAvailable />
            </SchoolDashboardShell>
        );
    }

    return (
        <SchoolDashboardShell needsAssessmentResults>
            <PageHeader
                title={headerTitle}
                subtitle={headerSubtitle}
            />

            {/* Info strip */}
            <Box sx={{
                display: 'flex', flexWrap: 'wrap', gap: {xs: 2, md: 0},
                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)', mb: 3, overflow: 'hidden',
            }}>
                {dynamicInfo.map((i, idx) => (
                    <Box key={i.label} sx={{
                        flex: {xs: '1 1 40%', md: 1}, px: 2.5, py: 2,
                        borderRight: {md: idx < dynamicInfo.length - 1 ? '1px solid #E5E7EB' : 'none'},
                    }}>
                        <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '0.8px', textTransform: 'uppercase', mb: 0.5}}>
                            {i.label}
                        </Typography>
                        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>{i.value}</Typography>
                    </Box>
                ))}
            </Box>

            {/* Light KPI cards */}
            <Grid container spacing={2.5} sx={{mb: 3}}>
                {displayKpiCards.map((kpi, idx) => (
                    <Grid item xs={12} md={12 / displayKpiCards.length} key={kpi.label || idx}>
                        <StatCard
                            label={kpi.label}
                            value={kpi.value}
                            band={kpi.band}
                            bandColorOverride={kpi.bandColorOverride}
                            sub={kpi.sub}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Three panels */}
            <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                    <Panel title={areaScoresTitle}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2.5, mb: 2, flexWrap: 'wrap'}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${MARKER_PRIMARY}`}}/>
                                <Typography sx={{fontSize: '11px', color: '#6A7282'}}>National avg</Typography>
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
                                <Typography sx={{fontSize: '11px', color: '#6A7282'}}>Global avg</Typography>
                            </Box>
                        </Box>
                        {dynamicAreaScores.map((a) => (
                            <ScoreBar key={a.label} label={a.label} score={a.score} band={a.band} national={a.national} global={a.global} bandColorOverride={readinessBandColor(a.band)} suppressed={a.suppressed}/>
                        ))}
                        {dynamicContextScores.length > 0 && (
                            <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid var(--brand-border, #E5E7EB)' }}>
                                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: NAVY, mb: 2.5 }}>
                                    {c6aTitle}
                                </Typography>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 2.5, mb: 2, flexWrap: 'wrap'}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                        <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${MARKER_PRIMARY}`}}/>
                                        <Typography sx={{fontSize: '11px', color: '#6A7282'}}>National avg</Typography>
                                    </Box>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                        <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
                                        <Typography sx={{fontSize: '11px', color: '#6A7282'}}>Global avg</Typography>
                                    </Box>
                                </Box>
                                {dynamicContextScores.map((a) => (
                                    <ScoreBar key={a.label} label={a.label} score={a.score} band={a.band} national={a.national} global={a.global} suppressed={a.suppressed}/>
                                ))}
                            </Box>
                        )}
                    </Panel>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Panel title={topRisksTitle}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2.5, mb: 2, flexWrap: 'wrap'}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${MARKER_PRIMARY}`}}/>
                                <Typography sx={{fontSize: '11px', color: '#6A7282'}}>National avg</Typography>
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
                                <Typography sx={{fontSize: '11px', color: '#6A7282'}}>Global avg</Typography>
                            </Box>
                        </Box>
                        {dynamicTopRisks.map((r) => (
                            <ScoreBar key={r.label} label={r.label} score={r.pct} band={r.band} national={r.national} global={r.global} suppressed={r.suppressed} unit="percent"/>
                        ))}
                    </Panel>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Panel title={gradesTitle}>
                        <GradesTable rows={dynamicGrades}/>
                    </Panel>
                </Grid>
            </Grid>

            <GuidanceBanner text={guidanceText}/>
        </SchoolDashboardShell>
    );
};

export default SchoolSummaryPage;
